'use client';

import * as RadixToast from '@radix-ui/react-toast';
import { type RefObject } from 'react';

import { Icon } from '../../../core-components';

import type { HTMLAttributes, PropsWithChildren} from 'react';

type Props = HTMLAttributes<HTMLDivElement> &
  PropsWithChildren<{
    title?: string;
    ref?: RefObject<HTMLDivElement>;
  }>;

/**
 * Renders the content of a toast notification.
 *
 * This component displays a toast with an optional title and children content. It also uses
 * Radix UI's Toast components for accessibility and behavior.
 */
export const ToastRenderFunction = ({
  title,
  children,
  ref,
  ...rest
}: Props) => {
  return (
    <div
      {...rest}
      className="rounded-md bg-surface-white p-6 text-marine-high-emphasis shadow-xl"
      ref={ref}
    >
      <div className="space-y-2 md:relative md:ml-[1.5rem] md:pl-2">
        <RadixToast.Title asChild>
          <div className="flex gap-2 md:block">
            <div className="md:absolute md:left-[-1.5rem] md:size-[1.5rem] md:pt-1">
              <Icon name="security-pass" size="md" />
            </div>
            <div className="headline-sm flex-1 md:headline-md">{title}</div>
          </div>
        </RadixToast.Title>
        {children && (
          <RadixToast.Description asChild>
            <div className="text-base md:text-lg">{children}</div>
          </RadixToast.Description>
        )}
      </div>
    </div>
  );
};

/**
 * The main toast component, using `forwardRef` to forward the ref to the `ToastRenderFunction`.
 */
export const Toast = ToastRenderFunction;
